/**
 * Signing the APK in, from the app's side (brief §11.7, §15).
 *
 * A WebView has no `navigator.credentials`, so the app cannot run the passkey ceremony. It
 * opens a Custom Tab instead — a real browser with the real credential store — and polls for
 * the code to be approved. What comes back is a device-bound bearer token, stored where the
 * platform keeps such things.
 *
 * Two things race, and both have to exist. The App Link at `/app/paired?code=…` fires the
 * moment the browser redirects; the poll is the fallback for when the link is not verified,
 * which happens whenever `assetlinks.json` and the signing key disagree — and reports itself
 * as nothing at all.
 *
 * The App Link half was described here before it existed. Capacitor fired `appUrlOpen` into an
 * empty room — "No listeners found for event appUrlOpen", visible in `adb logcat` and nowhere
 * else — so the redirect only brought the app to the front and the poll did all the work. That
 * costs about two seconds in the good case and the whole pairing in the bad one: if Android
 * reclaims the app while the Custom Tab is in front of it, the code exists only in that URL.
 */

import { request, setDeviceToken } from './api.ts';
import {
  closeBrowser,
  isNative,
  launchUrl,
  onAppUrlOpen,
  openInBrowser,
  preferences,
} from './platform.ts';

const TOKEN_KEY = 'hotspotter.device.token';
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

/** Load a token stored by an earlier pairing, so the app comes back signed in. */
export async function restoreDeviceToken(): Promise<boolean> {
  const token = await preferences.get(TOKEN_KEY);
  if (token === null || token === '') return false;
  setDeviceToken(token);
  return true;
}

export async function forgetDeviceToken(): Promise<void> {
  setDeviceToken(undefined);
  await preferences.remove(TOKEN_KEY);
}

export type PairingProgress = { code: string; expiresAt: string };

/** The pairing code out of an App Link, or `undefined` if that is not what this URL is. */
function codeFromUrl(url: string): string | undefined {
  try {
    const parsed = new URL(url);
    if (!parsed.pathname.startsWith('/app/paired')) return undefined;
    const code = parsed.searchParams.get('code');
    return code === null || code === '' ? undefined : code;
  } catch {
    return undefined;
  }
}

/**
 * Exchange a code for a device token and remember it.
 *
 * `false` covers every way this can not-yet-work. The endpoint answers the same for "not
 * approved", "no such code" and "already claimed", so there is nothing here to branch on —
 * by design, and it is why the poll below can call this in a loop without interpreting it.
 */
async function claim(code: string): Promise<boolean> {
  try {
    const claimed = await request<{ token: string }>('/api/auth/pair/claim', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    await preferences.set(TOKEN_KEY, claimed.token);
    setDeviceToken(claimed.token);
    return true;
  } catch {
    return false;
  }
}

/**
 * Finish a pairing that an App Link started while the app was not running.
 *
 * Called once at start-up. Android hands a cold-started app its launch URL and nothing else,
 * so if the app was reclaimed while the Custom Tab was in front of it, this is the only place
 * the code still exists.
 */
export async function claimFromLaunchUrl(): Promise<boolean> {
  const url = await launchUrl();
  if (url === undefined) return false;
  const code = codeFromUrl(url);
  return code === undefined ? false : await claim(code);
}

/**
 * Run the whole flow: ask for a code, open the browser, wait for it to be claimed.
 *
 * Resolves `true` when the device is signed in. `onprogress` is called once, with the code,
 * so the app can show it — somebody looking at a Custom Tab asking them to approve "P7K2MQ4X"
 * should be able to see the same string on the screen behind it.
 */
export async function pairDevice(
  deviceName: string,
  onprogress?: (progress: PairingProgress) => void,
): Promise<boolean> {
  const started = await request<{ code: string; url: string; expiresAt: string }>(
    '/api/auth/pair/start',
    { method: 'POST', body: JSON.stringify({ deviceName }) },
  );
  onprogress?.({ code: started.code, expiresAt: started.expiresAt });

  // Listening starts before the browser opens: a verified App Link can come back faster than
  // a Custom Tab takes to appear, and a listener added afterwards would miss it.
  let returned = false;
  const stopListening = onAppUrlOpen((url) => {
    if (codeFromUrl(url) !== undefined) returned = true;
  });

  try {
    await openInBrowser(started.url);

    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      // Wait out the interval, or cut it short the moment the App Link lands.
      await new Promise<void>((resolve) => {
        const done = () => {
          clearInterval(watch);
          clearTimeout(timer);
          resolve();
        };
        const watch = setInterval(() => {
          if (returned) done();
        }, 50);
        const timer = setTimeout(done, POLL_INTERVAL_MS);
      });

      // Always the code this flow started with, never the one out of the URL. They are the
      // same string in the honest case — and an intent is something any app on the device can
      // send, so the one that arrived from outside is not the one to trust.
      if (await claim(started.code)) {
        if (isNative()) await closeBrowser();
        return true;
      }
    }
    return false;
  } finally {
    stopListening();
  }
}
